import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const LIMIT_ATTRIBUTE = 'data-calico-quantity-limit';
const VARIANT_ATTRIBUTE = 'data-calico-limited-variant-id';
const DIGITAL_PATTERN_MARKER = 'digital cross stitch pattern';
const pendingForms = new WeakSet();
let auditPromise = null;

function getRoute(name, fallback) {
  if (typeof Theme === 'undefined') return fallback;
  return Theme.routes?.[name] || fallback;
}

function getCartUrl() {
  return `${getRoute('cart_url', '/cart')}.json`;
}

function getChangeUrl() {
  return getRoute('cart_change_url', '/cart/change');
}

function getItemCount(cart) {
  if (Number.isFinite(cart?.item_count)) return cart.item_count;
  return (cart?.items || []).reduce((total, item) => total + Number(item.quantity || 0), 0);
}

function getLimit(element) {
  const value = Number(element.getAttribute(LIMIT_ATTRIBUTE));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function getVariantId(form, element) {
  const input = form.querySelector('input[name="id"]');
  if (input instanceof HTMLInputElement && input.value) return input.value;
  return element.getAttribute(VARIANT_ATTRIBUTE);
}

function isLimitedCartItem(item) {
  const text = [
    item.product_type,
    item.product_title,
    item.title,
    item.handle,
    item.url,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    text.includes(DIGITAL_PATTERN_MARKER) ||
    text.includes('cross stitch pattern pdf') ||
    text.includes('cross-stitch-pattern-pdf')
  );
}

async function fetchCart() {
  const response = await fetch(getCartUrl(), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index);
}

function syncQuantitySelectors(variantId, limit) {
  for (const selector of document.querySelectorAll('[data-variant-id]')) {
    if (!(selector instanceof HTMLElement) || selector.dataset.variantId !== String(variantId)) continue;

    const input = selector.querySelector('input[data-cart-quantity]');
    if (!(input instanceof HTMLInputElement)) continue;

    input.max = String(limit);
    input.setAttribute('data-cart-quantity', String(limit));
    if (Number(input.value || 0) > limit) input.value = String(limit);

    if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
      selector.updateCartQuantity();
    } else if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }
  }
}

async function changeLineQuantity(item, limit) {
  const sections = getCartSectionIds();
  const body = {
    id: item.key || item.id || item.variant_id,
    quantity: limit,
    sections_url: window.location.pathname,
  };

  if (sections.length > 0) body.sections = sections.join(',');

  const promise = fetch(getChangeUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  })
    .then((response) => response.json())
    .then((cart) => {
      if (cart.errors) throw new Error(cart.errors);
      syncQuantitySelectors(item.variant_id, limit);
      return {
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: getItemCount(cart),
          source: 'calico-digital-quantity-limit',
          didError: false,
        },
      };
    });

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: item.key || item.id || String(item.variant_id), quantity: limit }],
      promise,
    })
  );

  await promise;
}

async function enforceCart(cart) {
  const limitedItems = (cart?.items || []).filter((item) => isLimitedCartItem(item));

  for (const item of limitedItems) {
    const limit = 1;
    if (Number(item.quantity || 0) > limit) {
      await changeLineQuantity(item, limit);
    } else {
      syncQuantitySelectors(item.variant_id, limit);
    }
  }
}

function scheduleCartAudit(delay = 0) {
  if (auditPromise) return auditPromise;

  auditPromise = new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  })
    .then(fetchCart)
    .then(enforceCart)
    .catch((error) => {
      console.warn('[calico] Unable to enforce digital quantity limit:', error);
    })
    .finally(() => {
      auditPromise = null;
    });

  return auditPromise;
}

async function openCart() {
  if (window.Shopify?.actions?.openCart) {
    try {
      await window.Shopify.actions.openCart();
      return;
    } catch (error) {
      console.warn('[calico] Unable to open cart drawer:', error);
    }
  }

  window.location.href = getRoute('cart_url', '/cart');
}

async function guardFormSubmit(form, element, submitter) {
  const limit = getLimit(element);
  const variantId = getVariantId(form, element);

  if (!limit || !variantId) {
    form.dataset.calicoQuantityLimitBypass = 'true';
    form.requestSubmit();
    return;
  }

  pendingForms.add(form);

  try {
    const cart = await fetchCart();
    const line = (cart.items || []).find((item) => String(item.variant_id) === String(variantId));

    if (line && Number(line.quantity || 0) >= limit) {
      await enforceCart(cart);
      syncQuantitySelectors(variantId, limit);
      await openCart();
      return;
    }

    form.dataset.calicoQuantityLimitBypass = 'true';
    if (submitter instanceof HTMLElement && submitter.closest('form') === form) {
      form.requestSubmit(submitter);
    } else {
      form.requestSubmit();
    }
  } catch (error) {
    console.warn('[calico] Unable to check digital quantity limit:', error);
    form.dataset.calicoQuantityLimitBypass = 'true';
    form.requestSubmit();
  } finally {
    pendingForms.delete(form);
  }
}

document.addEventListener(
  'submit',
  (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    const form = event.target;
    const element = form.hasAttribute(LIMIT_ATTRIBUTE) ? form : form.closest(`[${LIMIT_ATTRIBUTE}]`);
    if (!(element instanceof HTMLElement)) return;
    if (!getLimit(element)) return;

    if (form.dataset.calicoQuantityLimitBypass === 'true') {
      delete form.dataset.calicoQuantityLimitBypass;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (pendingForms.has(form)) return;
    guardFormSubmit(form, element, event.submitter);
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail }) => {
      const items = detail?.items || [];
      if (items.some((item) => isLimitedCartItem(item) && Number(item.quantity || 0) > 1)) {
        enforceCart({ items });
        return;
      }
      scheduleCartAudit(100);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[calico] Cart quantity audit skipped:', error);
    });
});

document.addEventListener('DOMContentLoaded', () => scheduleCartAudit());
window.addEventListener('pageshow', () => scheduleCartAudit());
